import { PartOfUnit, Precision, Qualifier } from '../domain/enums.js';
import { z } from '../zod.js';
const Int = z.number().int();
/**
 * The typed parts of one side of a time point (`time_point.lo_parts`, `hi_parts`; baseline
 * section 3 and section 6 "Typed parts"). Months, seasons, eras and intercalary months are
 * referenced by key; `part` is the first, middle or last third of the unit. Every part is
 * optional: `{}` on a side means "without limit on that side" (1.K-1). Numeric parts are
 * integers on the wire. Whether the parts make a date at their precision, and whether a value is
 * in range for the calendar, is the engine's question (its review reasons), not this schema's.
 */
export const TimePointParts = z.strictObject({
    era: z.string().optional(),
    year: Int.optional(),
    month: z.string().optional(),
    day: Int.optional(),
    hour: Int.optional(),
    minute: Int.optional(),
    season: z.string().optional(),
    week: Int.optional(),
    intercalary: z.string().optional(),
    part: PartOfUnit.optional(),
});
/** True for a parts object that holds no part: a key whose value is `undefined` is no part. */
function isEmptyParts(parts) {
    return (typeof parts === 'object' &&
        parts !== null &&
        Object.values(parts).every((value) => value === undefined));
}
/**
 * The wire shape of an authored time point (a `time_point` row's authored columns). At least one
 * part is present: `loParts` may be empty (open below) only when `hiParts` is set and not empty,
 * and `hiParts` may be empty (open above) only when `loParts` is not.
 */
export const TimePointV1 = z
    .strictObject({
    loParts: TimePointParts,
    hiParts: TimePointParts.nullable(),
    precision: Precision,
    qualifier: Qualifier,
    label: z.string(),
    rawInput: z.string(),
})
    .superRefine((point, ctx) => {
    if (!isEmptyParts(point.loParts))
        return;
    if (point.hiParts === null) {
        ctx.addIssue({
            code: 'custom',
            message: 'a single time point needs at least one part',
            path: ['loParts'],
        });
    }
    else if (isEmptyParts(point.hiParts)) {
        ctx.addIssue({
            code: 'custom',
            message: 'a range cannot be open on both sides',
            path: ['hiParts'],
        });
    }
});
const KeyMap = z.record(z.string(), z.string().nullable());
/**
 * A calendar edit's remap (`calendar_version.remap`; the `remap` of `calendar.diff` and
 * `calendar.commit`): for months, intercalary months, seasons and eras, an old key to its new key,
 * or to null when the key was removed. A key absent from a map is unchanged; every map is
 * optional (baseline section 3; 1.K-1 deliverable 15).
 */
export const CalendarRemap = z.strictObject({
    months: KeyMap.optional(),
    intercalary: KeyMap.optional(),
    seasons: KeyMap.optional(),
    eras: KeyMap.optional(),
});
//# sourceMappingURL=time-point.js.map